import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Calendar, CheckCircle, Clock, Eye, FileText, XCircle, AlertTriangle } from 'react-feather';
import { ConfigToolbar, FilterChip, ConfigEmptyState } from '../../components/ConfigPages';
import { EtlTiles, EtlLoading, EtlBanner, FilterSelect } from '../../components/EtlPages';
import FilesTable from '../../components/FileTracking/FilesTable';
import FilesCardList from '../../components/FileTracking/FilesCardList';
import { MobileFilterBar, ChipSelect } from '../../components/FileTracking/EtlMobileKit';
import { useIsMobile } from '../../hooks/use-mobile';
import CurrentStatusDetailPanel from '../../components/FileTracking/CurrentStatusDetailPanel';
import FileTrackingCalendar from '../../components/FileTracking/FileTrackingCalendar';
import HistoricalDetailPanel from '../../components/FileTracking/HistoricalDetailPanel';
import {
  getFileTrackingHistory,
  getFileTrackingCurrentStatus,
  updateFileTrackingHistoryRecord,
  clearManualReviewFlag,
} from '../../utils/api';

const ITEMS_PER_PAGE = 15;
const HISTORY_DAYS = 60;

const TILE_DEFS = [
  { key: 'processed',    label: 'Processed',    icon: CheckCircle,   tone: 'ok' },
  { key: 'pending',      label: 'In flight',    icon: Clock,         tone: 'info' },
  { key: 'needs_review', label: 'Needs review', icon: Eye,           tone: 'info' },
  { key: 'missed',       label: 'Not received', icon: AlertTriangle, tone: 'warn' },
  { key: 'failed',       label: 'Failed',       icon: XCircle,       tone: 'error' },
];

/**
 * Status tab — "did the files we expect today actually land?", plus the
 * 60-day calendar behind the same tab.
 *
 * Today and Calendar used to be two sibling tabs asking the same question at
 * two zoom levels, which pushed the tab strip to eight entries. They are one
 * tab with a range toggle now; the toggle sits in the toolbar next to the
 * filters it shares.
 */
export default function StatusTab({
  currentUser,
  scope,
  refreshTrigger,
  onConfigureTracking,
  onViewFile,
  onEmailRecord,
  onRecordsChange,
  registerRefresh,
  registerExport,
}) {
  const { orgsToQuery, accessibleHotelIds, hotelIdsKey, locationsLoading, locationNameById, scopeReady } = scope;
  const isMobile = useIsMobile();

  // ?range=calendar lets the legacy /file-tracking/historical links (and the
  // alert emails that carry them) land on the calendar directly.
  const [searchParams] = useSearchParams();
  const [range, setRange] = useState(searchParams.get('range') === 'calendar' ? 'calendar' : 'today');
  const [currentRaw, setCurrentRaw] = useState([]);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [fileTypeFilter, setFileTypeFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' });

  const requestId = useRef(0);
  const loadedKeys = useRef({ today: null, calendar: null });

  const primaryOrgCode = orgsToQuery[0];
  const scopeKey = `${orgsToQuery.join(',')}|${hotelIdsKey}|${refreshTrigger ?? ''}`;

  const enrich = useCallback((records) => records.map((record) => ({
    ...record,
    metadata: {
      ...record.metadata,
      locationName: locationNameById.get(record.hotelId) || record.metadata?.locationName,
    },
  })), [locationNameById]);

  const loadData = useCallback(async (force = false) => {
    // One request against the settled scope, rather than one per resolution step.
    if (!scopeReady || locationsLoading) return;
    if (!accessibleHotelIds.length || !primaryOrgCode) {
      setCurrentRaw([]);
      setHistoryRecords([]);
      setLoading(false);
      return;
    }
    if (!force && loadedKeys.current[range] === scopeKey) return;

    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      if (range === 'today') {
        const response = await getFileTrackingCurrentStatus(primaryOrgCode, { hotelIds: accessibleHotelIds });
        if (requestId.current !== id) return;
        setCurrentRaw(enrich(response?.records || []));
      } else {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - HISTORY_DAYS);
        const response = await getFileTrackingHistory(primaryOrgCode, {
          hotelIds: accessibleHotelIds,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
        });
        if (requestId.current !== id) return;
        setHistoryRecords(enrich(response?.records || []));
      }
      loadedKeys.current[range] = scopeKey;
    } catch (err) {
      if (requestId.current !== id) return;
      console.error(`Error loading file tracking ${range}:`, err);
      setError(range === 'today' ? 'Failed to load current file status.' : 'Failed to load file history.');
    } finally {
      if (requestId.current === id) setLoading(false);
    }
  }, [range, scopeReady, locationsLoading, accessibleHotelIds, primaryOrgCode, scopeKey, enrich]);

  useEffect(() => { loadData(); }, [loadData]);

  /**
   * Normalise the SQL rows into what the table and detail pane read.
   * `received` collapses into `pending` here: from the "is my data in?" point
   * of view an in-flight file is not yet done. MISSED_FILE sentinels are
   * failures of arrival, not of processing — they get their own status so the
   * Failed tile stays a real error count.
   */
  const records = useMemo(() => currentRaw.map((record) => {
    let status = record.status === 'received' ? 'pending' : record.status;
    const isMissedFileSentinel = record.dlq?.lastErrorCategory === 'MISSED_FILE';
    if (isMissedFileSentinel && status === 'failed') status = 'missed';
    return {
      id: record.id,
      stateId: record.stateId,
      hotelId: record.hotelId,
      hotelCode: record.metadata?.hotelCode || record.hotelCode,
      orgId: record.orgId,
      fileType: record.fileType,
      displayName: record.displayName || record.fileType,
      effectiveDate: record.effectiveDate,
      status,
      isMissedFileSentinel,
      dlq: record.dlq || null,
      metadata: { locationName: record.metadata?.locationName || `Hotel ${record.hotelId}` },
      reception: record.reception || {},
      processing: record.processing || {},
      manualReview: record.manualReview,
      alerts: record.alerts || { history: [] },
      expectations: record.expectations,
      tracking: {
        // lastIngestion is the all-time answer for this (hotel, file type) — it
        // fills "Last Received" when today's row is missed and has no reception.
        lastReceived: record.reception?.receivedAt || record.lastIngestion?.lastReceivedAt,
        lastSuccessfullyProcessed: record.processing?.completedAt || record.lastIngestion?.lastProcessedAt,
        lastRowsProcessed: record.processing?.rowsInserted,
      },
      locationName: record.metadata?.locationName || `Hotel ${record.hotelId}`,
    };
  }), [currentRaw]);

  const counts = useMemo(() => {
    const c = { all: records.length, processed: 0, pending: 0, needs_review: 0, missed: 0, failed: 0 };
    for (const r of records) {
      if (r.status === 'received') c.pending += 1;
      else if (c[r.status] != null) c[r.status] += 1;
    }
    return c;
  }, [records]);

  // The page-level flow rail reads today's counts from here.
  useEffect(() => { if (onRecordsChange) onRecordsChange(counts); }, [counts, onRecordsChange]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const out = records.filter((r) => {
      if (q) {
        const hay = `${r.metadata?.locationName || ''} ${r.fileType || ''} ${r.displayName || ''} ${r.status || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter && r.status !== statusFilter) return false;
      if (locationFilter && String(r.hotelId) !== String(locationFilter)) return false;
      if (fileTypeFilter && r.fileType !== fileTypeFilter) return false;
      return true;
    });

    // Most-urgent first by default, so the row that needs a human is on screen
    // without scrolling.
    const STATUS_ORDER = { needs_review: 0, failed: 1, missed: 2, pending: 3, received: 4, processed: 5 };
    const val = (r) => {
      switch (sortConfig.key) {
        case 'locationName': return r.metadata?.locationName || '';
        case 'status': return STATUS_ORDER[r.status] ?? 6;
        case 'receivedAt': {
          const t = r.reception?.receivedAt || r.tracking?.lastReceived;
          return t ? new Date(t).getTime() : 0;
        }
        default: return r[sortConfig.key] || '';
      }
    };
    out.sort((a, b) => {
      const av = val(a); const bv = val(b);
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  }, [records, searchQuery, statusFilter, locationFilter, fileTypeFilter, sortConfig]);

  const fileTypeOptions = useMemo(() => {
    const seen = new Map();
    for (const r of records) if (r.fileType && !seen.has(r.fileType)) seen.set(r.fileType, r.displayName || r.fileType);
    return [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [records]);

  const locationOptions = useMemo(() => {
    const seen = new Map();
    for (const r of records) if (r.hotelId && !seen.has(String(r.hotelId))) seen.set(String(r.hotelId), r.metadata?.locationName || `Hotel ${r.hotelId}`);
    return [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [records]);

  const handleSort = (key) => setSortConfig((prev) => ({
    key,
    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
  }));

  const handleMarkReviewed = async (record) => {
    try {
      await updateFileTrackingHistoryRecord(record.id, record.orgId, {
        manualReview: { cleared: true },
        updatedBy: currentUser?.email || 'unknown',
      });
      // The Cosmos tracking flag is legacy — clear it best-effort so stale
      // flags don't linger, but never fail the action on it.
      clearManualReviewFlag(record.hotelId, record.fileType, currentUser?.email || 'unknown')
        .catch((err) => console.warn('Legacy review-flag clear failed (non-fatal):', err));
      toast.success('Marked as reviewed');
      loadData(true);
    } catch (err) {
      console.error('Error clearing review:', err);
      toast.error('Failed to clear review');
    }
  };

  const exportCsv = useCallback(() => {
    const rows = range === 'today' ? filtered : historyRecords;
    if (!rows.length) { toast.error('No records to export'); return; }
    const headers = ['Location', 'Hotel ID', 'File Type', 'Status', 'Received At', 'Processed At'];
    // Local time with year — the table shows local times, so the export must
    // match; raw UTC ISO here confused every reader.
    const t = (iso) => (iso ? new Date(iso).toLocaleString() : '');
    const body = rows.map((r) => [
      r.metadata?.locationName || r.locationName || '',
      r.hotelId, r.fileType, r.status,
      t(r.reception?.receivedAt), t(r.processing?.completedAt),
    ]);
    const csv = [headers, ...body]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    link.download = `file-tracking-${range}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success(`Exported ${rows.length} records`);
  }, [range, filtered, historyRecords]);

  // The shell owns the Refresh and Export buttons; hand it the implementations.
  useEffect(() => { registerRefresh && registerRefresh(() => loadData(true)); }, [registerRefresh, loadData]);
  useEffect(() => { registerExport && registerExport(exportCsv); }, [registerExport, exportCsv]);

  const rangeChips = (
    <>
      <FilterChip active={range === 'today'} onClick={() => setRange('today')} title="Today's expected files">
        <FileText size={13} /> Today
      </FilterChip>
      <FilterChip active={range === 'calendar'} onClick={() => setRange('calendar')} title={`Last ${HISTORY_DAYS} days`}>
        <Calendar size={13} /> Calendar
      </FilterChip>
    </>
  );

  if (error) {
    return <EtlBanner tone="error" title="Could not load">{error}</EtlBanner>;
  }

  const busy = loading || locationsLoading;

  if (range === 'calendar') {
    return (
      <>
        {isMobile ? (
          <MobileFilterBar bar={<span className="cp-meta">{`${historyRecords.length.toLocaleString()} records · last ${HISTORY_DAYS} days`}</span>}>
            {rangeChips}
          </MobileFilterBar>
        ) : (
          <div className="it-frame" style={{ marginBottom: 14 }}>
            <ConfigToolbar chips={rangeChips} meta={`${historyRecords.length.toLocaleString()} records · last ${HISTORY_DAYS} days`} />
          </div>
        )}
        {busy && !historyRecords.length ? (
          <EtlLoading>Loading file history…</EtlLoading>
        ) : (
          <div className="ep-split ep-detail-left">
            <FileTrackingCalendar
              records={historyRecords}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              loading={busy}
            />
            <HistoricalDetailPanel
              selectedDate={selectedDate}
              records={historyRecords}
              onEmailRecord={onEmailRecord}
              onViewFile={onViewFile}
              onClearReview={handleMarkReviewed}
              onConfigure={onConfigureTracking}
              currentUser={currentUser}
              onBack={() => setSelectedDate(null)}
            />
          </div>
        )}
      </>
    );
  }

  const tiles = (
    <EtlTiles
      tiles={TILE_DEFS.map((t) => ({ ...t, n: counts[t.key] || 0 }))}
      value={statusFilter}
      onChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
    />
  );

  const setSearch = (v) => { setSearchQuery(v); setCurrentPage(1); };
  const setLocation = (v) => { setLocationFilter(v); setCurrentPage(1); };
  const setFileType = (v) => { setFileTypeFilter(v); setCurrentPage(1); };

  // Phones: status tiles become a scrolling filter row, the toolbar becomes a
  // full-width search over a chip row, and the table becomes a card list whose
  // detail opens full-screen.
  if (isMobile) {
    return (
      <>
        <div className="ep-m-tiles">{tiles}</div>
        <MobileFilterBar
          search={{ value: searchQuery, onChange: setSearch, placeholder: 'Search files, locations, statuses…' }}
          bar={<span className="cp-meta">{`${filtered.length.toLocaleString()} of ${records.length.toLocaleString()} files`}</span>}
        >
          {rangeChips}
          <ChipSelect value={locationFilter} options={locationOptions} onChange={setLocation} placeholder="All locations" ariaLabel="Filter by location" />
          <ChipSelect value={fileTypeFilter} options={fileTypeOptions} onChange={setFileType} placeholder="All file types" ariaLabel="Filter by file type" />
        </MobileFilterBar>

        {busy && !records.length ? (
          <EtlLoading>Loading file status…</EtlLoading>
        ) : !records.length ? (
          <div className="it-frame">
            <ConfigEmptyState
              title="No tracked files in this scope"
              body="Files appear here once a feed is configured for a location you can see. Add one on the Feeds tab."
            />
          </div>
        ) : (
          <FilesCardList records={filtered} selectedRecord={selectedRecord} onSelectRecord={setSelectedRecord} />
        )}

        {selectedRecord ? (
          <div className="ep-m-screen" role="dialog" aria-modal="true" aria-label={selectedRecord.displayName || selectedRecord.fileType}>
            <CurrentStatusDetailPanel
              mobile
              record={selectedRecord}
              onEmail={onEmailRecord}
              onViewFile={onViewFile}
              onMarkReviewed={handleMarkReviewed}
              onConfigure={onConfigureTracking}
              onClose={() => setSelectedRecord(null)}
            />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      {tiles}
      <div className="it-frame" style={{ marginBottom: 14 }}>
        <ConfigToolbar
          search={{ value: searchQuery, onChange: setSearch, placeholder: 'Search files, locations, statuses…' }}
          chips={rangeChips}
          meta={`${filtered.length.toLocaleString()} of ${records.length.toLocaleString()}`}
        >
          <FilterSelect
            value={locationFilter}
            options={locationOptions}
            onChange={setLocation}
            placeholder="All locations"
            clearable
            minWidth={170}
            ariaLabel="Filter by location"
          />
          <FilterSelect
            value={fileTypeFilter}
            options={fileTypeOptions}
            onChange={setFileType}
            placeholder="All file types"
            clearable
            minWidth={170}
            ariaLabel="Filter by file type"
          />
        </ConfigToolbar>
      </div>

      {busy && !records.length ? (
        <EtlLoading>Loading file status…</EtlLoading>
      ) : !records.length ? (
        <div className="it-frame">
          <ConfigEmptyState
            title="No tracked files in this scope"
            body="Files appear here once a feed is configured for a location you can see. Add one on the Feeds tab."
          />
        </div>
      ) : (
        <div className="ep-split">
          <FilesTable
            records={filtered}
            selectedRecord={selectedRecord}
            onSelectRecord={setSelectedRecord}
            onViewFile={onViewFile}
            onConfigure={onConfigureTracking}
            sortConfig={sortConfig}
            onSort={handleSort}
            currentPage={currentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
          <CurrentStatusDetailPanel
            record={selectedRecord}
            onEmail={onEmailRecord}
            onViewFile={onViewFile}
            onMarkReviewed={handleMarkReviewed}
            onConfigure={onConfigureTracking}
            onClose={() => setSelectedRecord(null)}
          />
        </div>
      )}
    </>
  );
}
